import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal, ChapterLabel } from "./Reveal";

const POINTS = [
  {
    role: "For teachers",
    line: "A new way to explain — concepts appear where the lesson happens.",
  },
  {
    role: "For students",
    line: "A new way to understand — see it, explore it, respond through it.",
  },
  {
    role: "For the classroom",
    line: "Everything stays real. AuraXR simply adds a layer.",
  },
];

const SLIDES = [
  {
    img: "/images/final-cta.jpg",
    label: "The Kit",
    caption: "One teacher glass, one AuraRing, and a class set of team glasses.",
  },
  {
    img: "/images/teachers.jpg",
    label: "The Teacher",
    caption: "Explaining through AR glasses — the concept, made spatial.",
  },
  {
    img: "/images/journey-understand.jpg",
    label: "The Students",
    caption: "Solving the assignment through their glasses, together.",
  },
  {
    img: "/images/journey-reflect.jpg",
    label: "The Dashboard",
    caption: "The teacher sees who understood — and who needs support.",
  },
];

export default function WhatIsAuraXR() {
  const [idx, setIdx] = useState(0);
  const prev = () => setIdx((idx - 1 + SLIDES.length) % SLIDES.length);
  const next = () => setIdx((idx + 1) % SLIDES.length);
  const slide = SLIDES[idx];

  return (
    <section
      id="solution"
      data-testid="solution-section"
      className="relative py-14 sm:py-20 border-t border-white/[0.05]"
    >
      <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <ChapterLabel number="02" label="The Solution" />
            <Reveal delay={0.1}>
              <h2
                data-testid="solution-heading"
                className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.15] tracking-tight text-[#FFFDF9]"
              >
                Meet <span className="italic text-[#38BDF8]">AuraXR</span>.
                <br />
                Concepts, made <span className="italic text-[#38BDF8]">visible</span>.
              </h2>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="mt-4 max-w-md text-sm text-slate-400 leading-relaxed">
                AuraXR is a concept platform of AI-powered AR glasses for the classroom. A safe,
                subtle addition to the lesson. Never a replacement for it.
              </p>
            </Reveal>

            <div className="mt-8 space-y-5">
              {POINTS.map((p, i) => (
                <Reveal key={p.role} delay={0.1 + i * 0.06}>
                  <div data-testid={`solution-point-${i}`} className="flex items-baseline gap-4">
                    <span className="font-display italic text-[#38BDF8]/70 text-sm shrink-0">
                      0{i + 1}
                    </span>
                    <p className="text-sm text-slate-300 leading-relaxed">
                      <span className="text-[#FFFDF9]">{p.role}</span> — {p.line}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal delay={0.2}>
            <div className="relative" data-testid="solution-carousel">
              <div className="relative aspect-[4/3] overflow-hidden rounded-2xl border border-white/[0.08]">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={slide.img + idx}
                    src={slide.img}
                    alt={slide.label}
                    initial={{ opacity: 0, scale: 1.03 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0 h-full w-full object-cover"
                    draggable={false}
                  />
                </AnimatePresence>
                <div className="absolute inset-0 bg-gradient-to-t from-[#050811]/70 via-transparent to-transparent pointer-events-none" />
                <div className="absolute bottom-0 left-0 p-5">
                  <p data-testid="solution-slide-label" className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8]">
                    {slide.label}
                  </p>
                  <p className="mt-1.5 font-display text-base text-[#FFFDF9] max-w-xs leading-snug">
                    {slide.caption}
                  </p>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between">
                <div className="flex gap-1.5">
                  {SLIDES.map((s, i) => (
                    <button
                      key={s.label}
                      data-testid={`solution-dot-${i}`}
                      onClick={() => setIdx(i)}
                      aria-label={s.label}
                      className={`h-1 rounded-full transition-all duration-500 ${
                        i === idx ? "w-8 bg-[#38BDF8]" : "w-3 bg-slate-700 hover:bg-slate-500"
                      }`}
                    />
                  ))}
                </div>
                <div className="flex gap-2">
                  <button
                    data-testid="solution-prev"
                    onClick={prev}
                    aria-label="Previous image"
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-slate-300 hover:border-[#38BDF8] hover:text-[#38BDF8] transition-colors duration-300"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    data-testid="solution-next"
                    onClick={next}
                    aria-label="Next image"
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-slate-300 hover:border-[#38BDF8] hover:text-[#38BDF8] transition-colors duration-300"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
